/**
 * Excel Export Utility
 * Functions for exporting data to Excel format
 */

import { format } from 'date-fns';
import logger from './logger';
import { isPeriodAccrual } from './scheduleComparison';

// Number formatting for extracts: '.' decimal separator, no thousands separator.
// CSVs are consumed by Excel/Sheets which apply their own display formatting;
// emitting raw numeric strings (no grouping) keeps cells numeric and avoids
// breaking SUM/sort/filter. Returns '' for null/undefined.
const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '';
  return num.toFixed(decimals);
};

// Encode a single CSV cell. Numeric-looking cells are emitted unquoted so Excel
// treats them as numbers (SUM/sort works); everything else is quoted with
// embedded double-quotes escaped.
const csvCell = (cell) => {
  if (cell === null || cell === undefined) return '';
  const s = String(cell);
  if (s === '') return '';
  if (/^-?\d+(\.\d+)?$/.test(s)) return s;
  return `"${s.replace(/"/g, '""')}"`;
};

const csvRow = (row) => row.map(csvCell).join(',');

/**
 * Export amortization schedule to Excel
 * @param {Array} schedule - Amortization schedule data
 * @param {Object} contract - Contract details
 */
export const exportAmortizationSchedule = (schedule, contract) => {
  if (!schedule || schedule.length === 0) {
    return false;
  }

  // Determine flow type for export
  const getFlowType = (narration) => {
    if (!narration) return '';
    if (narration.includes('Drawdown') || narration.includes('Opening Balance') || narration.includes('Injection')) return 'IN';
    if (narration.includes('Payment') || narration.includes('Fee') || narration.includes('Duty') || narration.includes('Withdrawal')) return 'OUT';
    if (narration.includes('Accrual')) return 'ACC';
    return '';
  };

  // Determine status for export
  const getStatus = (entry) => entry.status || '';

  // Check for WHT data — gate on a non-zero WHT amount on at least one row.
  // Matches the predicate used in AmortizationScheduleTable.js so the column
  // appears in both surfaces under exactly the same conditions.
  const hasWhtData = schedule.some(e => Math.abs(parseFloat(e.wht_amount) || 0) > 0);
  const hasGrossUp = schedule.some(e => e.wht_gross_up);
  const whtRateEntry = schedule.find(e => e.wht_rate_applied && parseFloat(e.wht_rate_applied) > 0);
  const whtRate = whtRateEntry ? parseFloat(whtRateEntry.wht_rate_applied) : 0;

  // Create CSV content (Excel-compatible) - matches table columns
  const headers = [
    'Sequence',
    'From',
    'To',
    'Payment Date',
    'Flow',
    'Narration',
    'Status',
    'Rate %',
    'Days',
    'Capital Amount',
    'Capital Variance',
    'Interest Amount',
    'Interest Variance',
    'Withholding Tax',
    ...(hasWhtData ? ['WHT Mode'] : []),
    'Payment Amount',
    'Total',
    'Outstanding Balance',
  ];

  // Build contract header section
  const contractInfo = [
    ['Contract Reference:', contract?.reference || 'N/A'],
    ['Investor:', contract?.investor_name || 'N/A'],
    ['Capital Amount:', `${contract?.currency_code || ''} ${formatNumber(contract?.capital_amount || 0)}`],
    ['Interest Rate:', `${contract?.interest_rate || 0}%`],
    ['Deposit Date:', contract?.deposit_date ? format(new Date(contract.deposit_date), 'dd MMM yyyy') : 'N/A'],
    ['Maturity Date:', contract?.maturity_date ? format(new Date(contract.maturity_date), 'dd MMM yyyy') : 'N/A'],
    ...(hasWhtData ? [
      ['Withholding Tax Rate:', whtRate > 0 ? `${whtRate.toFixed(2)}%` : 'None'],
      ['Withholding Tax Gross-Up:', hasGrossUp ? 'Yes - Company Expense' : 'No - Deducted from Investor'],
    ] : []),
    [''],
  ];

  // Outstanding Balance is the engine-written value persisted on each row — exported
  // as-is, never recomputed. A prior client-side walker was replaced because on a
  // multi-investor schedule (flat per-investor rows, one per investor per period) a
  // single cumulative accumulator interleaves investors, so mid-period it was neither
  // a tranche balance nor a period total. Each row now carries its own investor's
  // tranche balance, matching what was submitted for approval.

  // Convert schedule data to CSV rows
  const rows = schedule.map((entry, idx) => {
    const capital = parseFloat(entry.capital_amount) || 0;
    const interest = parseFloat(entry.interest_amount) || 0;
    const wht = parseFloat(entry.wht_amount) || 0;
    // Total = investor take-home: capital + net_interest_amount.
    // For GU rows net = gross interest. For NG rows net = gross − wht.
    // Fallback to interest_amount when net_interest_amount is absent
    // (matches frontend's netSafe behaviour).
    const net = parseFloat(entry.net_interest_amount);
    const netSafe = Number.isFinite(net) ? net : interest;
    const total = capital + netSafe;
    // WHT Mode tag: GU / NG / '' for any row with non-zero WHT (accrual or payment).
    // Use abs so a negative carried-accrual WHT still tags its mode.
    const whtMode = Math.abs(wht) > 0
      ? (entry.wht_gross_up ? 'GU' : 'NG')
      : '';
    return [
      idx + 1,
      entry.period_start ? format(new Date(entry.period_start), 'dd MMM yyyy') : '',
      entry.period_end ? format(new Date(entry.period_end), 'dd MMM yyyy') : '',
      entry.payment_date ? format(new Date(entry.payment_date), 'dd MMM yyyy') : '',
      getFlowType(entry.narration),
      entry.narration || '',
      getStatus(entry),
      // Rate is accrual-only, mirroring the on-screen Rate column (AmortizationScheduleTable /
      // ApprovalDetailModal): a payment row settles a balance and carries no rate of its own.
      // This export exists to reconcile against the screen, so the two must not diverge.
      isPeriodAccrual(entry.narration) && entry.interest_rate ? parseFloat(entry.interest_rate).toFixed(2) : '',
      entry.days_in_period || '',
      formatNumber(entry.capital_amount),
      // Capital Variance (T108): adjusted − calculated when capital is overridden
      // (original_capital_amount holds the engine value); blank otherwise.
      (entry.has_override && entry.original_capital_amount != null)
        ? formatNumber(parseFloat(entry.capital_amount || 0) - parseFloat(entry.original_capital_amount || 0))
        : '',
      formatNumber(entry.interest_amount),
      // Interest Variance (T106/T108): adjusted − calculated for any row carrying
      // a stamped original_interest_amount (payment-line apportioned edit, or a
      // legacy accrual edit); blank otherwise.
      (entry.has_override && entry.original_interest_amount != null)
        ? formatNumber(parseFloat(entry.interest_amount || 0) - parseFloat(entry.original_interest_amount || 0))
        : '',
      wht !== 0 ? formatNumber(wht) : '',
      ...(hasWhtData ? [whtMode] : []),
      formatNumber(entry.payment_amount),
      total !== 0 ? formatNumber(total) : '',
      formatNumber(entry.outstanding_balance),
    ];
  });

  // Combine all sections
  const csvContent = [
    ...contractInfo,
    headers,
    ...rows,
  ]
    .map(csvRow)
    .join('\n');

  // Download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `Amortization_Schedule_${contract?.reference || 'export'}_${format(new Date(), 'yyyyMMdd')}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Export contract register to Excel
 * @param {Array} contracts - Contract data
 */
/**
 * Contract Register CSV.
 *
 * @param contracts                    the filtered rows currently on screen
 * @param options.includeBreakdown     false (default) = one row per contract;
 *                                     true = each multi-investor contract is followed
 *                                     by one row per investor.
 */
export const exportContractRegister = (contracts, options = {}) => {
  if (!contracts || contracts.length === 0) {
    return false;
  }
  const { includeBreakdown = false } = options;

  const headers = [
    'Row Type',
    'Reference',
    'External Reference',
    // On a Contract row this is the aggregated list of all investors; on an
    // Investor row it is that single investor. Investor Count disambiguates.
    'Investor',
    'Investor Count',
    'Multi Investor',
    'Issuer',
    'Product',
    'Currency',
    'Interest Rate %',
    'Deposit Date',
    'Maturity Date',
    'Capital Amount',
    'Share %',
    'Total Interest',
    'Total Contract Value',
    'Contract Capital Amount',
    'Contract Total Interest',
    'Contract Total Value',
    'Status',
    'Created Date',
  ];

  // EVERY row is self-describing: contract context (reference, issuer, product,
  // dates, rate, status) is REPEATED on per-investor rows rather than left blank,
  // so the sheet survives sorting and filtering — a blank-parent layout breaks the
  // moment a user sorts by any column, orphaning the children from their contract.
  //
  // ⚠ Row Type is therefore the guard against double-counting. Because the Capital
  // Amount / Total Interest / Total Contract Value columns now carry the INVESTOR's
  // figures on an 'Investor' row and the CONTRACT's on a 'Contract' row, summing a
  // column across the whole sheet counts multi-investor contracts twice. Filter
  // Row Type = 'Contract' (or use the Contract * columns, which hold the contract
  // total on every row) before totalling.
  const rows = [];
  contracts.forEach((contract) => {
    const investors = Array.isArray(contract.investors) ? contract.investors : [];
    const isMulti = !!contract.is_multi_investor;
    // Contract-level figures, repeated verbatim on every row of this contract.
    const contractContext = [
      contract.reference || '',
      contract.user_reference || '',
    ];
    const contractDescriptors = [
      contract.investor_name || '',
      contract.investor_count ?? '',
      isMulti ? 'Yes' : 'No',
      contract.issuer_name || '',
      contract.product_type || '',
      contract.currency_code || '',
      contract.interest_rate || '',
      contract.deposit_date ? format(new Date(contract.deposit_date), 'dd MMM yyyy') : '',
      contract.maturity_date ? format(new Date(contract.maturity_date), 'dd MMM yyyy') : '',
    ];
    const contractTotals = [
      formatNumber(contract.capital_amount),
      formatNumber(contract.total_interest),
      formatNumber(contract.total_contract_value),
    ];
    const contractTail = [
      contract.status || '',
      contract.created_at ? format(new Date(contract.created_at), 'dd MMM yyyy') : '',
    ];

    rows.push([
      'Contract',
      ...contractContext,
      ...contractDescriptors,
      formatNumber(contract.capital_amount),
      // A single-investor contract is 100% by definition — state it rather than
      // leaving the reader to infer it from a blank cell.
      isMulti ? '' : '100%',
      formatNumber(contract.total_interest),
      formatNumber(contract.total_contract_value),
      ...contractTotals,
      ...contractTail,
    ]);

    if (includeBreakdown && isMulti && investors.length > 0) {
      investors.forEach((inv) => {
        rows.push([
          'Investor',
          ...contractContext,
          // This row's own investor, not the aggregated list.
          `${inv.investor_name || ''}${inv.pending_from_contract_id ? ' (Pending)' : ''}`,
          ...contractDescriptors.slice(1),
          formatNumber(inv.capital_amount),
          inv.percentage != null ? `${inv.percentage}%` : '',
          // Never emit 0 where the split is unknown — see interest_is_per_investor.
          contract.interest_is_per_investor
            ? formatNumber(inv.total_interest)
            : 'n/a - not attributable',
          contract.interest_is_per_investor
            ? formatNumber((parseFloat(inv.capital_amount) || 0) + (parseFloat(inv.total_interest) || 0))
            : 'n/a - not attributable',
          ...contractTotals,
          ...contractTail,
        ]);
      });
    }
  });

  // With the breakdown off every row is a Contract row, so the Row Type column and the
  // duplicated "Contract *" total columns carry no information — drop them rather than
  // ship a file with three constant columns.
  const CONTRACT_ONLY_DROP = ['Row Type', 'Contract Capital Amount', 'Contract Total Interest', 'Contract Total Value'];
  const keep = includeBreakdown
    ? headers.map((_, i) => i)
    : headers.map((h, i) => (CONTRACT_ONLY_DROP.includes(h) ? -1 : i)).filter((i) => i >= 0);
  const outHeaders = keep.map((i) => headers[i]);
  const outRows = rows.map((r) => keep.map((i) => r[i]));

  const csvContent = [
    [`Contract Register - Generated ${format(new Date(), 'dd MMM yyyy HH:mm')}`],
    [''],
    outHeaders,
    ...outRows,
  ]
    .map(csvRow)
    .join('\n');

  downloadCSV(csvContent, `Contract_Register_${format(new Date(), 'yyyyMMdd')}.csv`);
};

/**
 * Export payment schedule to Excel
 * @param {Array} payments - Payment schedule data
 */
export const exportPaymentSchedule = (payments) => {
  if (!payments || payments.length === 0) {
    return false;
  }

  const headers = [
    'Contract Reference',
    'Investor',
    'Payment Date',
    'Type',
    'Interest Amount',
    'Capital Amount',
    'Total Payment',
    'Currency',
    'Status',
  ];

  const rows = payments.map((payment) => [
    payment.contract_reference || '',
    payment.investor_name || '',
    payment.payment_date ? format(new Date(payment.payment_date), 'dd MMM yyyy') : '',
    payment.type || '',
    formatNumber(payment.interest_amount),
    formatNumber(payment.capital_amount),
    formatNumber(payment.payment_amount),
    payment.currency_code || '',
    payment.status || 'Pending',
  ]);

  const csvContent = [
    [`Payment Schedule - Generated ${format(new Date(), 'dd MMM yyyy HH:mm')}`],
    [''],
    headers,
    ...rows,
    [''],
    ['Summary'],
    ['Total Payments:', payments.length],
    ['Total Amount:', formatNumber(payments.reduce((sum, p) => sum + (p.payment_amount || 0), 0))],
  ]
    .map(csvRow)
    .join('\n');

  downloadCSV(csvContent, `Payment_Schedule_${format(new Date(), 'yyyyMMdd')}.csv`);
};

/**
 * Export investor summary to Excel
 * @param {Array} investorData - Investor summary data
 */
export const exportInvestorSummary = (investorData) => {
  if (!investorData || investorData.length === 0) {
    return false;
  }

  const headers = [
    'Investor',
    'Type',
    'Country',
    'Currency',
    'Total Contracts',
    'Active Contracts',
    'Total Capital',
    'Total Interest',
    'Total Contract Value',
    'Status',
  ];

  const rows = investorData.map((investor) => [
    investor.name || '',
    investor.type || '',
    investor.domicile_country_name || '',
    investor.currency_code || '',
    investor.total_contracts || 0,
    investor.active_contracts || 0,
    formatNumber(investor.total_capital),
    formatNumber(investor.total_interest),
    formatNumber(investor.total_contract_value),
    investor.active ? 'Active' : 'Inactive',
  ]);

  const csvContent = [
    [`Investor Summary - Generated ${format(new Date(), 'dd MMM yyyy HH:mm')}`],
    [''],
    headers,
    ...rows,
  ]
    .map(csvRow)
    .join('\n');

  downloadCSV(csvContent, `Investor_Summary_${format(new Date(), 'yyyyMMdd')}.csv`);
};

/**
 * Export generic data to Excel
 * @param {Array} data - Array of objects
 * @param {String} filename - Output filename
 * @param {String} title - Report title
 */
export const exportToExcel = (data, filename, title = '') => {
  if (!data || data.length === 0) {
    return false;
  }

  // Get headers from first object
  const headers = Object.keys(data[0]);

  const rows = data.map((item) =>
    headers.map((header) => {
      const value = item[header];

      // Format dates
      if (value instanceof Date) {
        return format(value, 'dd MMM yyyy');
      }

      // Format numbers
      if (typeof value === 'number' && !Number.isInteger(value)) {
        return value.toFixed(2);
      }

      return value || '';
    })
  );

  const csvContent = [
    title ? [title] : [],
    title ? [''] : [],
    headers,
    ...rows,
  ]
    .filter((row) => row.length > 0)
    .map(csvRow)
    .join('\n');

  downloadCSV(csvContent, filename);
};

/**
 * Helper function to format numbers for CSV export
 */
const formatNumForExport = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'number') return value;
  return formatNumber(value, 2);
};

/**
 * Helper function to download CSV file
 */
const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

/**
 * Export audit trail to Excel
 * @param {Array} auditData - Audit trail data
 */
export const exportAuditTrail = (auditData) => {
  if (!auditData || auditData.length === 0) {
    return false;
  }

  const headers = [
    'ID',
    'Timestamp',
    'User',
    'Action',
    'Table',
    'Record ID',
    'Created By',
    'Amended By',
    'Old Values',
    'New Values',
  ];

  const rows = auditData.map((audit) => [
    audit.id || '',
    audit.changed_at ? format(new Date(audit.changed_at), 'dd MMM yyyy HH:mm:ss') : '',
    audit.changed_by || '',
    audit.action || '',
    audit.table_name || '',
    audit.record_id || '',
    audit.old_values?.created_by || audit.new_values?.created_by || '',
    audit.new_values?.updated_by || audit.old_values?.updated_by || '',
    audit.old_values ? JSON.stringify(audit.old_values) : '',
    audit.new_values ? JSON.stringify(audit.new_values) : '',
  ]);

  const csvContent = [
    [`Audit Trail - Generated ${format(new Date(), 'dd MMM yyyy HH:mm')}`],
    [''],
    headers,
    ...rows,
  ]
    .map(csvRow)
    .join('\n');

  downloadCSV(csvContent, `Audit_Trail_${format(new Date(), 'yyyyMMdd')}.csv`);
};

/**
 * Export current vs long-term portions report to Excel
 * @param {Array} data - Portion report data
 */
export const exportCurrentLongTerm = (data) => {
  if (!data || data.length === 0) {
    return false;
  }

  const headers = [
    'Contract Reference',
    'External Reference',
    'Investor',
    'Product Type',
    'Currency',
    'Capital Amount',
    'Rate %',
    'Deposit Date',
    'Maturity Date',
    'Paid Principal',
    'Paid Interest',
    'Paid WHT',
    'Paid Total',
    'Current Principal',
    'Current Interest',
    'Current WHT',
    'Current Total',
    'Long-Term Principal',
    'Long-Term Interest',
    'Long-Term WHT',
    'Long-Term Total',
    'Total Outstanding',
  ];

  const rows = data.map((row) => [
    row.contract_reference || '',
    row.user_reference || '',
    row.investor_name || '',
    row.product_type || '',
    row.currency_code || '',
    formatNumber(row.capital_amount),
    row.interest_rate ? parseFloat(row.interest_rate).toFixed(2) : '',
    row.deposit_date ? format(new Date(row.deposit_date), 'dd MMM yyyy') : '',
    row.maturity_date ? format(new Date(row.maturity_date), 'dd MMM yyyy') : '',
    formatNumber(row.paid_principal),
    formatNumber(row.paid_interest),
    formatNumber(row.paid_wht),
    formatNumber(row.paid_total),
    formatNumber(row.current_principal),
    formatNumber(row.current_interest),
    formatNumber(row.current_wht),
    formatNumber(row.current_total),
    formatNumber(row.long_term_principal),
    formatNumber(row.long_term_interest),
    formatNumber(row.long_term_wht),
    formatNumber(row.long_term_total),
    formatNumber(row.total_outstanding),
  ]);

  // Summary totals by currency
  const currencies = [...new Set(data.map((r) => r.currency_code))];
  const summaryRows = currencies.map((ccy) => {
    const subset = data.filter((r) => r.currency_code === ccy);
    const sum = (field) => subset.reduce((acc, r) => acc + (parseFloat(r[field]) || 0), 0);
    return [
      `Total (${ccy})`, '', '', '', ccy, '',  '', '', '',
      formatNumber(sum('paid_principal')),
      formatNumber(sum('paid_interest')),
      formatNumber(sum('paid_wht')),
      formatNumber(sum('paid_total')),
      formatNumber(sum('current_principal')),
      formatNumber(sum('current_interest')),
      formatNumber(sum('current_wht')),
      formatNumber(sum('current_total')),
      formatNumber(sum('long_term_principal')),
      formatNumber(sum('long_term_interest')),
      formatNumber(sum('long_term_wht')),
      formatNumber(sum('long_term_total')),
      formatNumber(sum('total_outstanding')),
    ];
  });

  const csvContent = [
    [`Current vs Long-Term Portions - Generated ${format(new Date(), 'dd MMM yyyy HH:mm')}`],
    [''],
    headers,
    ...rows,
    [''],
    ['Summary'],
    ...summaryRows,
  ]
    .map(csvRow)
    .join('\n');

  downloadCSV(csvContent, `Current_LongTerm_Portions_${format(new Date(), 'yyyyMMdd')}.csv`);
};

/**
 * Export rolled contracts audit report to Excel
 * @param {Array} data - Rolled contracts data
 */
export const exportRolledContracts = (data) => {
  if (!data || data.length === 0) {
    return false;
  }

  const headers = [
    'Original Ref',
    'Original Ext Ref',
    'Investor',
    'Original Capital',
    'Currency',
    'Original Maturity',
    'Original Status',
    'New Ref',
    'New Ext Ref',
    'Rolled Amount',
    'New Funds',
    'New Capital',
    'New Deposit Date',
    'New Maturity',
    'New Status',
    'Roll Date',
  ];

  const rows = data.map((row) => [
    row.original_contract_ref || '',
    row.original_user_ref || '',
    row.original_investor_name || '',
    formatNumber(row.original_capital_amount),
    row.original_currency_code || '',
    row.original_maturity_date ? format(new Date(row.original_maturity_date), 'dd MMM yyyy') : '',
    row.original_status || '',
    row.new_contract_ref || '',
    row.new_user_ref || '',
    formatNumber(row.rolled_amount),
    formatNumber(row.new_funds_amount),
    formatNumber(row.new_capital_amount),
    row.new_deposit_date ? format(new Date(row.new_deposit_date), 'dd MMM yyyy') : '',
    row.new_maturity_date ? format(new Date(row.new_maturity_date), 'dd MMM yyyy') : '',
    row.new_status || '',
    row.roll_date ? format(new Date(row.roll_date), 'dd MMM yyyy') : '',
  ]);

  const csvContent = [
    [`Rolled Contracts Report - Generated ${format(new Date(), 'dd MMM yyyy HH:mm')}`],
    [''],
    headers,
    ...rows,
  ]
    .map(csvRow)
    .join('\n');

  downloadCSV(csvContent, `Rolled_Contracts_${format(new Date(), 'yyyyMMdd')}.csv`);
};

/**
 * Export Rollover Fund Flow report
 */
export const exportRolloverFundFlow = (data) => {
  if (!data || data.length === 0) {
    return false;
  }

  const headers = [
    'Source Contract',
    'Ext Ref',
    'Status',
    'Maturity',
    'Source Capital',
    'Currency',
    'Investor',
    'Investor Capital',
    'WHT Rate',
    'Gross-Up',
    'Total Rolled',
    'Total Paid Out',
    'Remaining Unallocated',
    'Destinations',
  ];

  const rows = data.map((row) => {
    let destinations = '';
    try {
      const dests = typeof row.rolled_to_contracts === 'string'
        ? JSON.parse(row.rolled_to_contracts)
        : (row.rolled_to_contracts || []);
      destinations = dests.map((d) => `${d.reference} (${formatNumber(d.rolled_amount)})`).join('; ');
    } catch { /* ignore */ }

    return [
      row.source_contract_ref || '',
      row.source_user_ref || '',
      row.source_status || '',
      row.source_maturity_date ? format(new Date(row.source_maturity_date), 'dd MMM yyyy') : '',
      formatNumber(row.source_capital_amount),
      row.currency_code || '',
      row.investor_name || '',
      formatNumber(row.investor_capital),
      `${row.wht_rate || 0}%`,
      row.wht_gross_up ? 'Yes' : 'No',
      formatNumber(row.total_rolled),
      formatNumber(row.total_paid_out),
      formatNumber(row.remaining_unallocated),
      destinations,
    ];
  });

  const csvContent = [
    [`Rollover Fund Flow Report - Generated ${format(new Date(), 'dd MMM yyyy HH:mm')}`],
    [''],
    headers,
    ...rows,
  ]
    .map(csvRow)
    .join('\n');

  downloadCSV(csvContent, `Rollover_Fund_Flow_${format(new Date(), 'yyyyMMdd')}.csv`);
};

/**
 * Export Rollover Suspense report
 */
export const exportContractSuspense = (data) => {
  if (!data || data.length === 0) {
    return false;
  }

  const headers = [
    'Source Contract',
    'Ext Ref',
    'Finalisation Date',
    'Currency',
    'Investor',
    'Original Capital',
    'Accrued Interest',
    'WHT',
    'Net Value',
    'Rollover Suspense',
    'Allocated',
    'Remaining',
    'Paid Out (at finalisation)',
    'Paid Out (residual)',
    'Destinations',
  ];

  const rows = data.map((row) => {
    let destinations = '';
    try {
      const dests = typeof row.rolled_to_contracts === 'string'
        ? JSON.parse(row.rolled_to_contracts)
        : (row.rolled_to_contracts || []);
      destinations = dests.map((d) => `${d.reference} (${formatNumber(d.rolled_amount)})`).join('; ');
    } catch { /* ignore */ }

    return [
      row.source_contract_ref || '',
      row.source_user_ref || '',
      row.finalisation_date ? format(new Date(row.finalisation_date), 'dd MMM yyyy') : '',
      row.currency_code || '',
      row.investor_name || '',
      formatNumber(row.original_capital),
      formatNumber(row.accrued_interest),
      formatNumber(row.wht_amount),
      formatNumber(row.net_value),
      formatNumber(row.suspense_amount),
      formatNumber(row.allocated_to_new_contracts),
      formatNumber(row.remaining_unallocated),
      formatNumber(row.payout_amount),
      destinations,
    ];
  });

  const csvContent = [
    [`Rollover Suspense Report - Generated ${format(new Date(), 'dd MMM yyyy HH:mm')}`],
    [''],
    headers,
    ...rows,
  ]
    .map(csvRow)
    .join('\n');

  downloadCSV(csvContent, `Rollover_Suspense_${format(new Date(), 'yyyyMMdd')}.csv`);
};

/**
 * Export the dashboard Upcoming Events list (payments + repricing) for the
 * selected day-window. windowLabel (e.g. "0–30 Days", "Next 90 Days") is echoed
 * in the title + filename so the export reflects what's on screen. Mirrors the
 * columns shown in the dashboard table.
 */
export const exportUpcomingEvents = (events, windowLabel = 'Next 90 Days') => {
  if (!events || events.length === 0) {
    return false;
  }

  const headers = [
    'Contract Reference',
    'External Reference',
    'Investor',
    'Payment Date',
    'Type',
    'Amount',
    'Currency',
  ];

  const rows = events.map((e) => [
    e.reference || '',
    e.userReference || '',
    e.investor || '',
    e.paymentDate ? format(new Date(e.paymentDate), 'dd MMM yyyy') : '',
    e.type || '',
    e.amount != null ? formatNumber(e.amount) : '',
    e.currency || '',
  ]);

  const csvContent = [
    [`Upcoming Events (${windowLabel}) - Generated ${format(new Date(), 'dd MMM yyyy HH:mm')}`],
    [''],
    headers,
    ...rows,
    [''],
    ['Total Events:', events.length],
  ]
    .map(csvRow)
    .join('\n');

  const fileSuffix = windowLabel.replace(/[^0-9A-Za-z]+/g, '_').replace(/^_|_$/g, '');
  downloadCSV(csvContent, `Upcoming_Events_${fileSuffix}_${format(new Date(), 'yyyyMMdd')}.csv`);
};

/**
 * Export the dashboard "Maturing Soon" contract list for the selected day-window.
 * windowLabel is echoed in the title + filename. Mirrors exportUpcomingEvents;
 * rows come from the dashboard maturingSoon.list.
 */
export const exportMaturingContracts = (contracts, windowLabel = 'Next 90 Days') => {
  if (!contracts || contracts.length === 0) {
    return false;
  }

  const headers = [
    'Contract Reference',
    'External Reference',
    'Investor',
    'Maturity Date',
    'Status',
    'Amount',
    'Currency',
  ];

  const rows = contracts.map((c) => [
    c.reference || '',
    c.userReference || '',
    c.investor || '',
    c.maturityDate ? format(new Date(c.maturityDate), 'dd MMM yyyy') : '',
    c.status || '',
    c.amount != null ? formatNumber(c.amount) : '',
    c.currency || '',
  ]);

  const csvContent = [
    [`Maturing Soon (${windowLabel}) - Generated ${format(new Date(), 'dd MMM yyyy HH:mm')}`],
    [''],
    headers,
    ...rows,
    [''],
    ['Total Contracts:', contracts.length],
  ]
    .map(csvRow)
    .join('\n');

  const fileSuffix = windowLabel.replace(/[^0-9A-Za-z]+/g, '_').replace(/^_|_$/g, '');
  downloadCSV(csvContent, `Maturing_Contracts_${fileSuffix}_${format(new Date(), 'yyyyMMdd')}.csv`);
};

/**
 * Generate Excel workbook with multiple sheets (placeholder for future xlsx library)
 * For now, this creates separate CSV files
 */
export const exportWorkbook = (sheets, filename) => {
  logger.debug('Multi-sheet export would require xlsx library');
  logger.debug('For now, exporting each sheet separately...');

  sheets.forEach((sheet, index) => {
    if (sheet.data && sheet.data.length > 0) {
      exportToExcel(
        sheet.data,
        `${filename}_${sheet.name}_${format(new Date(), 'yyyyMMdd')}.csv`,
        sheet.name
      );
    }
  });
};
